import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import {
    AssetDetails,
    Button,
    Modal,
    ModalCloseButtonPositions,
    ModalPaddingSizes,
    ModalVariants,
    Price,
} from '@pkg/components';
import { ModalHeaderAlignments } from '@pkg/components/modals/primitives/Modal/components';
import { type Collectible } from '@pkg/graphql/codegen/graphql';
import { useAppSelector, usePrepareOrder, usePricing } from '@pkg/hooks';
import { type PrepareOrderType } from '@pkg/hooks/marketplace/orders/usePrepareOrder';
import { getBaseCurrencyFromChain, trimDecimals } from '@pkg/utils';

import { getNftDetailsUrl } from '@app/helpers';
import { YAT_PROFILE_PAGE } from '@app/routes/paths';

import { assetOrderModalCopy } from '../AssetOrder.copy';

import {
    ActionLink,
    Actions,
    ActionTile,
    ActionTitle,
    Summary,
    SummaryCount,
    SummaryHeader,
    TitleLarge,
    TitleMedium,
    Total,
    Totals,
    TotalValue,
} from './AssetOrderModal.style';

export interface AssetOrderModalProps {
    asset?: Collectible | null;
    type?: 'pre' | 'post';
    onClose: () => void;
    orderType?: PrepareOrderType;
}

export function AssetOrderModal({ asset, orderType, type = 'pre', onClose }: AssetOrderModalProps) {
    const { getPricingValues } = usePricing();
    const { prepareOrder: confirmOrder } = usePrepareOrder();
    const primaryYat = useAppSelector((state) => state.user?.primaryYat);
    const modalCopy = assetOrderModalCopy[orderType || 'list'];
    const navigate = useNavigate();
    const assetId = asset?.id || '';
    const [chain, contract, tokenId] = assetId.split('.') || [];

    const assetCurrency = getBaseCurrencyFromChain(chain);

    const assetPrice = asset?.orders?.find((order) => !!order.price)?.price;
    const { nativeValue, usdValue } = getPricingValues({ weiValue: assetPrice, currency: assetCurrency });

    const usdPrice = trimDecimals(usdValue);
    const hasInitiatedBuy = useRef(false);
    const [isLoadingPurchase, setIsLoadingPurchase] = useState(false);

    const handleSubmit = useCallback(() => {
        hasInitiatedBuy.current = true;
        setIsLoadingPurchase(true);
        if (orderType === 'buy') {
            confirmOrder().then(() => {
                setIsLoadingPurchase(false);
            });
        }
    }, [confirmOrder, orderType]);

    useEffect(() => {
        if (orderType === 'buy' && type === 'pre' && !isLoadingPurchase && !hasInitiatedBuy.current) {
            handleSubmit();
        }
    }, [handleSubmit, isLoadingPurchase, orderType, type]);

    const nftDetailsUrl = getNftDetailsUrl({
        contractAddress: contract,
        tokenId,
        chain,
    });
    const handleViewProfile = useCallback(() => {
        const profileUrl = primaryYat
            ? orderType !== 'buy'
                ? `/${primaryYat}/orders`
                : `/${primaryYat}/`
            : YAT_PROFILE_PAGE;
        onClose();
        navigate(profileUrl);
    }, [navigate, onClose, orderType, primaryYat]);

    const handleViewAsset = useCallback(() => {
        onClose();
        navigate(nftDetailsUrl);
    }, [navigate, nftDetailsUrl, onClose]);

    const pricingMarkup =
        orderType === 'buy' ? (
            <Totals>
                <Total>
                    <TitleMedium>Subtotal</TitleMedium>
                    <TotalValue>
                        <Price currency={assetCurrency} currencyPrice={nativeValue} usdPrice={usdPrice} />
                    </TotalValue>
                </Total>
                {/* TODO: Update gas fees */}
                {/* <CartSummary
                    items={[
                        {
                            label: 'Gas fees',
                            value: '$75.15',
                        },
                    ]}
                /> */}
            </Totals>
        ) : null;

    const preMarkup = (
        <>
            <Summary>
                <SummaryHeader>
                    <TitleMedium>Summary</TitleMedium>
                    <SummaryCount>1 Item</SummaryCount>
                </SummaryHeader>
                <AssetDetails
                    key={asset?.name}
                    name={asset?.name ?? ''}
                    collectionName={asset?.collection?.name}
                    imageUrl={asset?.imageUrl}
                />
            </Summary>
            {pricingMarkup}
            <ActionTile onClick={handleSubmit}>
                <ActionTitle>Go to your wallet</ActionTitle>
                <div>{modalCopy.actionText}</div>
            </ActionTile>
        </>
    );

    const postMarkup = useMemo(() => {
        return (
            <>
                <ActionTile key={asset?.name}>
                    <AssetDetails
                        name={asset?.name ?? ''}
                        collectionName={asset?.collection?.name}
                        imageUrl={asset?.imageUrl}
                        size="large"
                    />
                </ActionTile>

                {!isLoadingPurchase && (
                    <Actions>
                        <Button size="large" onClick={() => handleViewAsset()}>
                            {modalCopy.viewAssetCta}
                        </Button>
                        <ActionLink onClick={() => handleViewProfile()}>View on profile</ActionLink>
                    </Actions>
                )}
            </>
        );
    }, [asset, handleViewAsset, handleViewProfile, isLoadingPurchase, modalCopy.viewAssetCta]);

    const titleValue = useMemo(() => {
        if (type === 'pre') {
            return modalCopy.titlePre;
        } else {
            if (isLoadingPurchase) {
                return modalCopy.titleLoading;
            }
            return modalCopy.titlePost;
        }
    }, [isLoadingPurchase, modalCopy.titleLoading, modalCopy.titlePost, modalCopy.titlePre, type]);

    return (
        <Modal
            variant={ModalVariants.Simple}
            closeButtonPosition={ModalCloseButtonPositions.Header}
            padded
            paddingSize={ModalPaddingSizes.Large}
            onClose={onClose}
        >
            <Modal.Header align={ModalHeaderAlignments.Left}>
                <TitleLarge>{titleValue}</TitleLarge>
            </Modal.Header>

            <Modal.Layout>
                <Modal.Layout.Section>{type === 'post' ? postMarkup : preMarkup}</Modal.Layout.Section>
            </Modal.Layout>
        </Modal>
    );
}
